import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, API } from "@/lib/api";
import { ArrowLeft, CheckCircle2, XCircle, Clock, Loader2, Download, ExternalLink, FileText } from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  queued: { label: "In coda", icon: Clock, color: "#555555", bg: "rgba(85,85,85,0.1)" },
  running: { label: "In esecuzione", icon: Loader2, color: "#0F3D6B", bg: "rgba(15,61,107,0.1)", spin: true },
  succeeded: { label: "Completato", icon: CheckCircle2, color: "#15803d", bg: "rgba(34,197,94,0.12)" },
  failed: { label: "Errore", icon: XCircle, color: "#C8102E", bg: "rgba(200,16,46,0.1)" },
};

const EVENT_LABEL = {
  step_started: "Avvio passaggio",
  step_completed: "Passaggio completato",
  tool_call: "Chiamata strumento",
  log: "Log",
  error: "Errore",
};

export default function JobDetail() {
  const { id } = useParams();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const pollRef = useRef(null);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get(`/jobs/${id}`);
        setJob(data);
        setLoading(false);
        if (data.status === "succeeded" || data.status === "failed") {
          if (pollRef.current) clearInterval(pollRef.current);
        }
      } catch {
        setLoading(false);
      }
    };
    load();
    pollRef.current = setInterval(load, 2500);
    return () => pollRef.current && clearInterval(pollRef.current);
  }, [id]);

  if (loading) {
    return (
      <div className="space-y-4 max-w-4xl">
        <div className="adr-skeleton h-8 w-2/3" />
        <div className="adr-skeleton h-32 w-full" />
        <div className="adr-skeleton h-48 w-full" />
      </div>
    );
  }
  if (!job) return <div className="text-[#555555]">Lavoro non trovato.</div>;

  const meta = STATUS_META[job.status] || STATUS_META.queued;
  const Icon = meta.icon;

  const downloadFile = async (filename) => {
    try {
      const token = localStorage.getItem("adriai_token");
      const resp = await fetch(`${API}/jobs/${job.id}/files/${encodeURIComponent(filename)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error("Errore download");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Download fallito");
    }
  };

  const previewHtml = (filename) => {
    const token = localStorage.getItem("adriai_token");
    const w = window.open();
    if (!w) return;
    fetch(`${API}/jobs/${job.id}/preview/${encodeURIComponent(filename)}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.text())
      .then((html) => {
        w.document.open();
        w.document.write(html);
        w.document.close();
      });
  };

  return (
    <div className="max-w-4xl">
      <Link to="/lavori" className="inline-flex items-center gap-1.5 text-sm text-[#555555] hover:text-[#0F3D6B] mb-6 transition-colors" data-testid="back-to-jobs">
        <ArrowLeft className="w-4 h-4" />
        Torna ai lavori
      </Link>

      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-8">
        <div className="flex-1 min-w-0">
          <div className="adr-overline mb-2">{job.recipe_name}</div>
          <h1 className="serif text-2xl sm:text-3xl font-semibold tracking-tight text-[#1A1A1A] leading-tight" data-testid="job-title">
            {job.output_title || job.recipe_name}
          </h1>
        </div>
        <span className="adr-badge text-sm px-3 py-1.5" style={{ background: meta.bg, color: meta.color }} data-testid="job-status">
          <Icon className={`w-4 h-4 ${meta.spin ? "animate-spin" : ""}`} />
          {meta.label}
        </span>
      </div>

      {job.status === "failed" && job.error_message && (
        <div className="adr-card p-4 border-l-4 mb-6" style={{ borderLeftColor: "#C8102E", background: "rgba(200,16,46,0.04)" }}>
          <div className="text-sm font-semibold text-[#C8102E] mb-1">Errore</div>
          <div className="text-sm text-[#1A1A1A]">{job.error_message}</div>
        </div>
      )}

      {job.output_files?.length > 0 && (
        <div className="adr-card p-6 mb-6">
          <h2 className="serif text-lg font-semibold mb-4">Artefatti generati</h2>
          <div className="space-y-2.5">
            {job.output_files.map((f) => (
              <div key={f.name} className="flex items-center justify-between p-3 rounded-md border border-[#E2E8F0] bg-white" data-testid={`file-${f.name}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <FileText className="w-5 h-5 text-[#0F3D6B] flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-[#1A1A1A] truncate">{f.name}</div>
                    <div className="text-xs text-[#555555]">
                      {(f.size / 1024).toFixed(1)} KB · {f.kind}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {f.kind === "html" && (
                    <button
                      onClick={() => previewHtml(f.name)}
                      className="adr-btn-outline text-xs px-3 py-1.5"
                      data-testid={`preview-${f.name}`}
                    >
                      <ExternalLink className="w-3.5 h-3.5 inline mr-1" />
                      Anteprima
                    </button>
                  )}
                  <button
                    onClick={() => downloadFile(f.name)}
                    className="adr-btn-secondary text-xs px-3 py-1.5"
                    data-testid={`download-${f.name}`}
                  >
                    <Download className="w-3.5 h-3.5 inline mr-1" />
                    Scarica
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="adr-card p-6">
        <h2 className="serif text-lg font-semibold mb-4">Timeline esecuzione</h2>
        <ol className="relative border-l-2 border-[#E2E8F0] ml-2 space-y-4">
          {(job.events || []).map((ev, idx) => (
            <li key={ev.id || idx} className="pl-5 relative" data-testid={`event-${idx}`}>
              <span
                className="absolute -left-[7px] top-1 w-3 h-3 rounded-full"
                style={{ background: ev.event_type === "error" ? "#C8102E" : "#0F3D6B" }}
              />
              <div className="text-xs text-[#555555] mb-0.5">
                {new Date(ev.timestamp).toLocaleTimeString("it-IT")} · {EVENT_LABEL[ev.event_type] || ev.event_type}
              </div>
              <div className="text-sm text-[#1A1A1A]">
                {ev.payload?.message || ev.payload?.step || ev.payload?.tool || JSON.stringify(ev.payload)}
              </div>
            </li>
          ))}
          {(!job.events || job.events.length === 0) && (
            <li className="text-sm text-[#555555] pl-5">In attesa di eventi...</li>
          )}
        </ol>
      </div>
    </div>
  );
}
